import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import download_ios_icon from '../assets/download-ios.svg';
import download_android_icon from '../assets/download-android.png';

const Download: React.FC = () => {
  const purchaseSectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash === '#purchase' && purchaseSectionRef.current) {
      const headerOffset = 90; // Height of your sticky header in pixels
      const elementPosition = purchaseSectionRef.current.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - headerOffset;

      // Timeout to ensure the element is rendered before scrolling
      setTimeout(() => {
        // purchaseSectionRef.current?.scrollIntoView({ behavior: 'smooth' }); // Old method
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }, 100); // Increased timeout slightly for potentially complex layouts
    }
  }, []);

  return (
    <div className="flex flex-col items-center justify-center setup-guide bg-dark-blue text-gray-300 p-4 h-full w-[100em] pb-10">
      {/* Page Title */}
      <h1 className="text-4xl text-center text-gray-300 mb-4 mt-4">Download</h1>
      <p className="text-xl text-center text-gray-400 mb-6">
        Download Deck Hopper for all supported platforms.
      </p>
      <hr className="border-t border-gray-600 w-2/3 mb-8" />

      {/* Download Sections */}
      <div className="flex flex-col items-center justify-center max-w-[70em] w-full">

        {/* Hero Banner */}
        <section className="relative w-full max-w-[70em] bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-lg p-6 mb-6">
          <div className="w-full max-w-[70em] mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Unlock the Full Potential of Deck Hopper</h2>
            <p className="text-lg mb-6">
              Upgrade to Pro and gain access to exclusive features that enhance your SAMMI experience.
            </p>
            <div className="flex justify-center space-x-4">
              <a href="upgrade#upgrade-now">
                <button className="bg-white text-indigo-600 font-semibold py-2 px-4 rounded-3xl shadow-md hover:bg-gray-100"
                >
                  Upgrade Now
                </button>
              </a>
              <Link to="/features">
                <button className="bg-white text-indigo-600 font-semibold py-2 px-4  rounded-3xl shadow-md hover:bg-gray-100">
                  See All Features
                </button>
              </Link>
            </div>
          </div>
        </section>
        {/* Deck Hopper Android Version */}
        <section className="w-full bg-bgBorderBlue bg-opacity-30 rounded-lg shadow-lg p-6 mb-8">
          <h2 className="text-3xl mb-4 text-gray-300 text-center">Android Version</h2>
          <p className="mb-4 text-center">
            Download Deck Hopper for <b>Android</b> from the Google Play Store and enjoy a <b>10-day free trial</b>.
          </p>
          <p className="my-4 text-center text-sm text-gray-400 italic">This app is in maintenance mode. Critical bugs will still be fixed, but no new features are planned at this time.</p>
          <div className="flex justify-center">
            <a href="https://play.google.com/store/apps/details?id=ca.christinak.DeckHopper" target="_blank" rel="noopener noreferrer">
              <img
                alt="Get it on Google Play"
                src={download_android_icon}
                className="w-48 h-auto cursor-pointer hover:opacity-80 transition-opacity duration-300"
              />
            </a>
          </div>
        </section>

        {/* Deck Hopper iOS Version */}
        <section className="w-full bg-bgBorderBlue bg-opacity-30 rounded-lg shadow-lg p-6 mb-8">
          <h2 className="text-3xl mb-4 text-gray-300 text-center">iOS Version</h2>
          <p className="mb-4 text-center">
            Download Deck Hopper for <b>iOS</b> from the App Store enjoy the <b>Pro version</b> unlocked instantly.
            </p>
            <p className="my-4 text-center text-sm text-gray-400 italic">This app is no longer actively maintained. It may continue to work, but no further updates are planned and compatibility with future iOS versions is not guaranteed.</p>
            <p>
              <i> iOS purchases are separate from Android and Desktop purchases. If you have already purchased Deck Hopper on Android or Desktop, you will need to purchase it again on iOS.</i>
            </p>
            <div className="flex justify-center">
              <a href="https://apps.apple.com/us/app/deck-hopper/id6746429701" target="_blank" rel="noopener noreferrer">
                <img
                  alt="Get it on the App Store"
                  src={download_ios_icon}
                  className="w-48 h-auto cursor-pointer hover:opacity-80 transition-opacity duration-300"
                />
              </a>
            </div>
        </section>

        {/* Deck Hopper Desktop Version */}
        <section id="purchase" ref={purchaseSectionRef} className="w-full bg-bgBorderBlue bg-opacity-30 rounded-lg shadow-lg p-6 mb-8">
          <h2 className="text-3xl mb-4 text-gray-300 text-center">Desktop Version</h2>

          <p className="mb-4 text-center">
            Download Deck Hopper for <strong>Windows 10</strong>, <strong>macOS</strong>, or <strong>Linux</strong> and enjoy a
            <b> 10‑day free trial</b>.
          </p>
          <p className="my-4 text-center text-sm text-gray-400 italic">This app is in maintenance mode. Critical bugs will still be fixed, but no new features are planned at this time.</p>
          <p className="mb-4 text-center">
            Keep using it after your trial:
            <ol className="list-inside list-disc mt-2">
              <li>
                <s className="text-red-500 opacity-70">$4.99</s> $2.99 for{' '}
                <strong><a
                  href="https://christinak.itch.io/deck-hopper"
                  className="text-amber-500 hover:text-amber-400 underline font-bold"
                >
                  Deck Hopper Basic
                </a></strong> &nbsp;— pay once and use it forever.

              </li>
              <li>
                <s className="text-red-500 opacity-70">$7.99</s> $5.99 for&nbsp;
                <a
                  href="https://christinak.itch.io/deck-hopper-pro"
                  className="text-amber-500 hover:text-amber-400 underline font-bold"
                >
                  Deck Hopper Pro
                </a>
                &nbsp;— permanently removes ads and unlocks all advanced features.
              </li>
            </ol>
          </p>

          <div className="flex flex-col md:flex-row justify-center items-center gap-4 mt-6">
            <iframe
              title="Deck Hopper"
              src="https://itch.io/embed/2348723?border_width=0&amp;bg_color=122645&amp;fg_color=dddada&amp;link_color=2563eb&amp;border_color=122645"
              className="w-full md:w-[28rem] h-40 border-none"
            >
              <a href="https://christinak.itch.io/deck-hopper">Deck Hopper</a>
            </iframe>

            <iframe
              title="Deck Hopper Pro"
              src="https://itch.io/embed/3377571?border_width=0&amp;bg_color=122645&amp;fg_color=dddada&amp;link_color=2563eb&amp;border_color=122645"
              className="w-full md:w-[28rem] h-40 border-none"
            >
              <a href="https://christinak.itch.io/deck-hopper-pro">Deck Hopper Pro</a>
            </iframe>
          </div>

        </section>

        {/* Deck Hopper Advanced Extension */}
        <section className="w-full bg-bgBorderBlue bg-opacity-30 rounded-lg shadow-lg p-6 mb-8">
          <h2 className="text-3xl mb-4 text-gray-300 text-center">Deck Hopper Advanced Extension</h2>
          <p className="mb-4 text-center">
            To use some advanced features, you will need to install the Deck Hopper Advanced Extension. This extension is free to download and use; however, some features may require Deck Hopper Pro.
          </p>
          <div className="flex justify-center">
            <iframe
              className="w-2/3 h-40 border-none"
              title="Deck Hopper Advanced Extension"
              src="https://itch.io/embed/2655946?border_width=0&amp;bg_color=142032&amp;fg_color=dddada&amp;link_color=2563eb&amp;border_color=142032">
              <a href="https://christinak.itch.io/deck-hopper-advanced">Deck Hopper Advanced</a>
            </iframe>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Download;
